import React, { useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useApolloClient, useQuery, useMutation } from '@apollo/react-hooks';
import gql from 'graphql-tag';
import findLast from 'lodash/findLast';
import pick from 'lodash/pick';
import { useHistory } from 'react-router-dom';
import root from 'window-or-global';

import GoogleContext from 'data/services/google';
import GlobalContext from 'site/context';
import NotFoundPage from 'pages/404';
import Redirect from 'routing/redirect';
import ApplicationPage from 'site/application-page';
import Icon from 'common/icon';
import ContributePage from './contribute-page';

export const GET_CAMPAIGN = gql`
    query GetCampaign($slug: String!) {
        campaign: campaignBySlug(slug: $slug) {
            id
            slug
            name
            description

            isDonationOnly
            isComplete

            pledgePresets
            donatePresets

            effectiveEnableTips
            shouldDisplayBenefitInHero
            benefits

            metricName {
                singular
                plural
            }
            eventName {
                singular
                plural
            }

            owner {
                id
                firstName
                lastName
            }

            recipient {
                id
                name
                type
                collectAddresses
            }

            stats {
                performanceActualTotal
                performanceRemaining
                eventRemainingCount
                eventFinalizedCount
            }

            performances {
                id
                isFinal
            }

            thirdPartyTerms {
                statement
                extended
            }
        }
    }
`;

const GET_FUNDRAISERS = gql`
    query FundraisersByCampaign($id: ID!) {
        fundraiserList: fundraisersByCampaign(campaignId: $id, limit: -1) {
            items {
                id
                user {
                    id
                    firstName
                    lastName
                    username
                }
            }
        }
    }
`;

export const GET_ROLES = gql`
    query GetCampaignRoles($id: ID!) {
        my {
            roles: rolesFor(type: "campaign", id: $id)
            user {
                id
                username
            }
        }
    }
`;

export const GET_CONTRIBUTION = gql`
    query GetContribution($id: ID!) {
        contribution: contributionById(id: $id) {
            id
            invoices {
                id
                createdAt
                amount
                status
            }
        }
    }
`;

export const CREATE_CONTRIBUTION = gql`
    mutation CreateContribution($data: ContributionInput!) {
        createContribution(data: $data)
    }
`;

const ContributePageContainer = ({ match }) => {
    const history = useHistory();
    const client = useApolloClient();
    const [startedAtBeginning, setStartedAtBeginning] = useState(false);
    const { pageview } = useContext(GoogleContext);
    const { showFlash, getLockedContributionReferrer, setRecentContribution } = useContext(
        GlobalContext
    );

    // Fetch campaign data for entire process
    const campaignQuery = useQuery(GET_CAMPAIGN, {
        variables: { slug: match.params.slug },
    });
    const campaign = campaignQuery.data?.campaign;

    // Main mutation for creating contribution record
    const [createContribution] = useMutation(CREATE_CONTRIBUTION, {
        skip: !campaign?.id,
        awaitRefetchQueries: true,
        refetchQueries: [
            { query: GET_ROLES, variables: { id: campaign?.id } },
            { query: GET_CAMPAIGN, variables: { slug: campaign?.slug } },
        ],
    });

    const fundraisersQuery = useQuery(GET_FUNDRAISERS, {
        skip: !campaign?.id,
        variables: { id: campaign?.id },
    });
    const fundraisers = fundraisersQuery.data?.fundraiserList?.items ?? [];

    // Fetch user roles to campaign to determine if they
    // are a fundraiser
    const rolesQuery = useQuery(GET_ROLES, {
        skip: !campaign?.id,
        variables: { id: campaign?.id },
    });
    const roles = rolesQuery.data?.my?.roles ?? [];

    // Redirect to the first step on initial page load
    useEffect(() => {
        if (campaign && !rolesQuery.loading && !fundraisersQuery.loading && !startedAtBeginning) {
            setStartedAtBeginning(true);

            let firstStep = 'type';
            if (campaign.isDonationOnly) {
                firstStep = fundraisers.length ? 'fundraiser' : 'amount';
            }

            history.replace(`/${match.params.slug}/contribute/${firstStep}`);
        }
    }, [campaign, rolesQuery.loading, fundraisersQuery.loading, fundraisers]);

    // Scroll to the top every time the step changes
    useEffect(() => {
        root.scrollTo(500, 0);
    }, [match.params.step]);

    // Hide Zendesk widget
    useEffect(() => {
        root.zE(() => root.zE.hide());
        return () => root.zE(() => root.zE.show());
    }, []);

    const userReferrer = roles.includes('fundraiser') ? rolesQuery.data.my.user.username : null;

    // Check the local store for a referrer that the user came in on
    const lockedReferrer = campaign?.id ? getLockedContributionReferrer(campaign?.id) : null;

    // Show 404 page if campaign doesn't exist
    if (!campaignQuery.loading && !campaign) return <NotFoundPage />;

    // Redirect back to campaign with a flash message if it has
    // already ended
    if (campaign?.isComplete) {
        showFlash({
            type: 'info',
            title: 'This campaign has ended',
            message: 'Contributions to this campaign are no longer able to be made.',
            duration: 5000,
        });

        return <Redirect to={`/${campaign.slug}`} />;
    }

    const isLoading =
        campaignQuery.loading ||
        fundraisersQuery.loading ||
        rolesQuery.loading ||
        !startedAtBeginning;

    // While donations are processed immediately, they actually aren't
    // processed as part of this request, so we don't get any indication
    // whether it succeeded or not from the response. In order to let the
    // user know that their donation failed, we poll the status of their
    // pending contribution for up to 10 seconds to look for a
    // success/failure. In the event of no status change in that time, we
    // will display a message to the user letting them know it's taking
    // unusually long.
    const handleCreateContribution = async data => {
        const response = await createContribution({ variables: { data } });
        const contributionId = response.data?.createContribution;
        let completed = data.type === 'pledge';

        // If the user donated, we'll poll the invoice for 10 seconds to
        // determine if the payment succeeded or not
        if (data.type === 'donation') {
            let tries = 0;

            do {
                // Refetch the contribution
                const query = await client.query({
                    query: GET_CONTRIBUTION,
                    variables: { id: contributionId },
                    fetchPolicy: 'network-only',
                });

                if (query.error) throw query.error;

                const { status } = findLast(query.data.contribution.invoices, {
                    amount: data.amount,
                });

                // Check it's status
                if (status === 'paid') {
                    completed = true;
                } else if (['error', 'failed'].includes(status)) {
                    throw new Error('Donation failed to process');
                }

                // Wait 1 second
                await new Promise(resolve => setTimeout(resolve, 1000));

                tries += 1;
            } while (tries < 10 && !completed);
        }

        // Log event to GA (used in goal tracking)
        pageview(`/${campaign.slug}/contribute/thanks`);

        // Set some data for the campaign page to use once
        // the user is redirected
        await setRecentContribution(pick(data, ['campaign', 'type', 'amount']));

        return { completed };
    };

    return (
        <ApplicationPage header="minimal" footer={null}>
            {isLoading ? (
                <div className="flex-grow flex justify-center items-center text-21">
                    <Icon icon="spinner" className="text-gray-30" size="lg" spin />
                </div>
            ) : (
                <ContributePage
                    step={match.params.step}
                    campaign={campaign}
                    fundraisers={fundraisers}
                    referrer={lockedReferrer || userReferrer}
                    onContribute={handleCreateContribution}
                />
            )}
        </ApplicationPage>
    );
};

ContributePageContainer.propTypes = {
    match: PropTypes.shape({
        params: PropTypes.shape({
            slug: PropTypes.string.isRequired,
            step: PropTypes.string,
        }).isRequired,
    }).isRequired,
};

export default ContributePageContainer;
